import { Cell, Fill, Worksheet } from "exceljs";
import i18n from "i18n";
import { ERowType } from "common/enums";
import { IWBSRow } from "common/interfaces";
import { FirebaseDocument } from "common/models/FirebaseDocument";
import { WBSStore } from "modules/WBS/WBSStore";
import { WBSPropertiesStore } from "modules/WBS/WBSPropertiesStore";
import { CustomPropertyStore } from "modules/customProperties/CustomPropertyStore";
import { CustomPropertyModel } from "modules/customProperties/CustomPropertyModel";
import { borderThinStyle, fillLightGray, fontNormal } from "./ReportStyles";
import { createAlphabet, deepCopy } from "./ReportUtils";
import { monetaryFormat, numberFormat, percentageFormat } from "./constants";
import { divide, roundBy, sum, times } from "utils/DecimalUtils";

const ABC_COLOR = [
  { pMin: 0, pMax: 80, c: "ffff4500", toolbar: "Curva A - De 0% a 80%" },
  { pMin: 80, pMax: 95, c: "ffff8c00", toolbar: "Curva B - De 80,01% a 95%" },
  { pMin: 95, pMax: 101, c: "ffc0d147", toolbar: "Curva C - De 95,01% a 100%" },
];

const alphabet = [
  "K",
  "L",
  "M",
  "N",
  "O",
  "P",
  "Q",
  "R",
  "S",
  "T",
  "U",
  "V",
  "W",
  "X",
  "Y",
  "Z",
];

export async function getDataToSyntheticABCCurveServiceReport(
  worksheet: Worksheet
) {
  return getDataToABCCurveServiceReports(worksheet, WBSStore.dataTable);
}

export async function getDataToAnalyticABCCurveServiceReport(
  worksheet: Worksheet
) {
  return getDataToABCCurveServiceReports(worksheet, WBSStore.WBSRows);
}

async function getDataToABCCurveServiceReports(
  worksheet: Worksheet,
  servicesList: IWBSRow[]
) {
  const stageCustomProperties = CustomPropertyStore.getWBSCustomProperties();
  const letters = [...alphabet, ...createAlphabet()];

  worksheet = setColumnHeader(worksheet, stageCustomProperties, letters);

  const rows: IWBSRow[] = deepCopy(
    servicesList.filter((row) => row.data.rowType === ERowType.TASK)
  );
  const rules = WBSPropertiesStore.getRules(2);

  // Obtém as linhas  a serem exportadas
  const rowsToReport: IWBSRow[] = [];
  for (const row of rows) {
    // Obtém a quantidade da composição
    let quantity = row.data.quantity || 0;
    if (row.data.parentTask) {
      quantity = WBSStore.getRowAccumulatedQuantity(row.id);
    }

    // Atualiza o total e quantidade do serviço na lista a ser exportada,
    // sem duplicação de linhas.
    const currentRow = rowsToReport.find(
      (r) => r.data.contentId === row.data.contentId
    );

    if (currentRow) {
      currentRow.data.quantity = sum(currentRow.data.quantity || 0, quantity);
      currentRow.data.total = times(
        currentRow.data.unitCost || 0,
        currentRow.data.quantity || 0,
        rules
      );
    } else {
      row.data.quantity = quantity;
      row.data.total = times(row.data.unitCost || 0, quantity, rules);
      rowsToReport.push(row);
    }
  }
  rowsToReport.sort((a, b) => (b.data.total || 0) - (a.data.total || 0));

  // Obtém o total de todos os serviços
  const projectTotalCost = rowsToReport.reduce(
    (total, row) => sum(total, row.data.total || 0, rules),
    0
  );
  worksheet.getCell("I5").value = projectTotalCost;

  let percentageAcum = 0;
  let i = 0;

  // Adicionar os dados no corpo do relatório.
  for (const row of rowsToReport) {
    if (!row.data.description) continue;

    let percentage = divide(row.data.total || 0, projectTotalCost);
    percentage = times(percentage, 100);
    percentageAcum += percentage;

    const line = String(i + 13);

    worksheet.getCell(`A${line}`).value = row.data.rowCode || "";
    setValueCellStyle(worksheet.getCell(`A${line}`));

    worksheet.getCell(`B${line}`).value = row.data.description;
    setValueCellStyle(worksheet.getCell(`B${line}`));
    setValueCellStyle(worksheet.getCell(`C${line}`));
    setValueCellStyle(worksheet.getCell(`D${line}`));

    worksheet.getCell(`E${line}`).value = row.data.quantity || 0;
    worksheet.getCell(`E${line}`).numFmt = numberFormat;
    setValueCellStyle(worksheet.getCell(`E${line}`));

    worksheet.getCell(`F${line}`).value = row.data.unit || "";
    setValueCellStyle(worksheet.getCell(`F${line}`));

    worksheet.getCell(`G${line}`).value = roundBy(
      row.data.unitCost || 0,
      rules
    );
    worksheet.getCell(`G${line}`).numFmt = monetaryFormat;
    setValueCellStyle(worksheet.getCell(`G${line}`));

    worksheet.getCell(`H${line}`).value = roundBy(row.data.total || 0, rules);
    worksheet.getCell(`H${line}`).numFmt = monetaryFormat;
    setValueCellStyle(worksheet.getCell(`H${line}`));

    worksheet.getCell(`I${line}`).value = percentage;
    worksheet.getCell(`I${line}`).numFmt = percentageFormat;
    setValueCellStyle(worksheet.getCell(`I${line}`));

    worksheet.getCell(`J${line}`).value = percentageAcum;
    worksheet.getCell(`J${line}`).numFmt = percentageFormat;
    setValueCellStyle(worksheet.getCell(`J${line}`));

    // Adiciona a cor da porcentagem acumulada
    const [color] = ABC_COLOR.filter(
      (abc) => percentageAcum > abc.pMin && percentageAcum <= abc.pMax
    );
    const fillPercentage: Fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: {
        argb: color ? color.c : "FFEFEFEF",
      },
    };
    worksheet.getCell(`J${line}`).fill = fillPercentage;

    // Mergea as células da descrição
    worksheet.mergeCells(`B${line}:D${line}`);

    // Adiciona os valores das propriedades customizadas do serviço
    if (stageCustomProperties) {
      const rowCustomProperties = row.data.customProperties;
      stageCustomProperties.forEach((prop, index) => {
        setValueCellStyle(worksheet.getCell(letters[index] + line));

        if (rowCustomProperties) {
          worksheet.getCell(letters[index] + line).value =
            rowCustomProperties[prop.id] || "";
        }
      });
    }

    i++;
  }

  // Altera a largura das colunas
  worksheet.columns = [
    { key: "A", width: 10 },
    { key: "B", width: 20 },
    { key: "C", width: 20 },
    { key: "D", width: 20 },
    { key: "E", width: 20 },
    { key: "F", width: 20 },
    { key: "G", width: 20 },
    { key: "H", width: 20 },
    { key: "I", width: 20 },
    { key: "J", width: 20 },
  ];
  return worksheet;
}

function setColumnHeader(
  worksheet: Worksheet,
  stageCustomProperties: FirebaseDocument<CustomPropertyModel>[],
  letters: string[]
) {
  // Adicionada labels das colunas obrigatórias
  worksheet.getCell("A12").value = i18n.project.rightSideMenu.code;
  worksheet.getCell("B12").value = i18n.resources.description;
  worksheet.getCell("E12").value = i18n.resources.quantity;
  worksheet.getCell("F12").value = i18n.resources.unity;
  worksheet.getCell("G12").value = i18n.resources.unitCost;
  worksheet.getCell("H12").value = i18n.resources.total;
  worksheet.getCell("I12").value = "%";
  worksheet.getCell("J12").value = i18n.resources.cumulativePercentageSmall;

  // Aplica merge nas células
  worksheet.mergeCells("B12:D12");

  // Adicionar labels e estilos das propriedades customizadas
  if (CustomPropertyStore.getWBSCustomProperties().length) {
    stageCustomProperties.forEach((prop, index) => {
      worksheet.getCell(letters[index] + "12").value = prop.data.label;
    });
  }
  return worksheet;
}

function setValueCellStyle(cell: Cell) {
  cell.font = fontNormal;
  cell.border = borderThinStyle;
  cell.fill = fillLightGray;
  cell.alignment = { vertical: "middle" };
}



// WEBPACK FOOTER //
// ./src/modules/costPlan/assets/excelReports/abcCurveService.ts